import type { ThunkAction } from 'redux-thunk';
import type { AsyncThunk } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { createNewMatchOverride, fetchLicenses, fetchOverrides, fetchProject, fetchRevision, removeMatchOverride, triggerRebuild } from './api';
import type { Match, MatchOverride, Project, Task } from '../../types';
import type { Action } from './slice';
import * as GlobalLicenseCorrectionStore from './slice';
import type { ReduxStore } from '../store';
import Locator from '../../../shared/Locator';

export const { projectLookupError, clearLookupError } = GlobalLicenseCorrectionStore.actions;

const lookupProject =
  (projectQuery: string): ThunkAction<void, ReduxStore, never, Action> =>
    async (dispatch) => {
      const locator = new Locator(projectQuery);

      if (!locator.revision.value || locator.revision.value === '') {
        await dispatch(fetchProject(locator.toPackageString()));
      } else {
        await dispatch(fetchRevision(locator.toString()));
      }
    };

/** Looks up a project and it's licenses/matchOverrides for the LicenseCorrections card. */
export const lookupProjectAndLicenses =
  (projectQuery: string): ThunkAction<void, ReduxStore, never, Action> =>
    async (dispatch, getState: () => ReduxStore) => {
      if (GlobalLicenseCorrectionStore.selectors.projectSelector(getState()).isLoading) {
        return;
      }

      await dispatch(lookupProject(projectQuery));

      const project = GlobalLicenseCorrectionStore.selectors.projectSelector(getState());
      const revision = GlobalLicenseCorrectionStore.selectors.revisionSelector(getState());

      if (!project.value || !revision.value) {
        return;
      }

      if (!project.value.locator) {
        dispatch(projectLookupError('API Bug: Project has no locator!'));
        return;
      }

      if (!revision.value.locator) {
        dispatch(projectLookupError('API Bug: Head revision is missing a locator!'));
        return;
      }

      await Promise.all([
        dispatch(fetchOverrides(project.value.locator)),
        dispatch(fetchLicenses(revision.value.locator)),
      ]);
    };

export const removeMatch =
  (project: Project, match: Match): ThunkAction<void, ReduxStore, never, Action> =>
    async (dispatch, getState) => {
      await dispatch(createNewMatchOverride({
        componentProjectId: project.locator as string,
        newLicenseIdOrRemove: null,
        matchFilter: {
          type: 'hash',
          matchIdentityHash: match.matchIdentityHash,
          licenseId: match.licenseId,
          path: match.path,
        },
      }));

      const revisionValue = GlobalLicenseCorrectionStore.selectors.revisionSelector(getState());
      if (revisionValue.value && !revisionValue.isLoading) {
        await dispatch(lookupProjectAndLicenses(revisionValue.value.locator as string));
      }
    };

export const removeLicense =
  (project: Project, licenseId: string): ThunkAction<void, ReduxStore, never, Action> =>
    async (dispatch, getState) => {
      await dispatch(createNewMatchOverride({
        componentProjectId: project.locator as string,
        newLicenseIdOrRemove: null,
        matchFilter: {
          type: 'license',
          licenseId,
        },
      }));

      const revisionValue = GlobalLicenseCorrectionStore.selectors.revisionSelector(getState());
      if (revisionValue.value && !revisionValue.isLoading) {
        await dispatch(lookupProjectAndLicenses(revisionValue.value.locator as string));
      }
    };

export const revertOverride =
  (matchOverride: MatchOverride): ThunkAction<void, ReduxStore, never, Action> =>
    async (dispatch, getState) => {
      await dispatch(removeMatchOverride(matchOverride.id));

      const revisionValue = GlobalLicenseCorrectionStore.selectors.revisionSelector(getState());
      if (revisionValue.value && !revisionValue.isLoading) {
        await dispatch(lookupProjectAndLicenses(revisionValue.value.locator as string));
      }
    };

/** Kicks off a rebuild of the current revision */
export const rebuildRevision: AsyncThunk<Task, void, {state: ReduxStore}> =
  createAsyncThunk<Task, void>(
    'globalLicenseCorrections/rebuildRevision',
    async (_args, { getState }) => {
      const projectValue = GlobalLicenseCorrectionStore.selectors.projectSelector(getState() as ReduxStore);
      if (projectValue.isLoading || !projectValue.value?.locator) {
        throw new Error('Cannot rebuild while loading.');
      }

      return triggerRebuild(projectValue.value.locator);
    },
  );

export type NewLicenseOverride = {
  copyright: string;
  licenseId: string;
  licenseText: string;
};

/** Creates a new-license override and reloads license data. */
export const addLicense =
  (newLicense: NewLicenseOverride): ThunkAction<void, ReduxStore, never, Action> =>
    async (dispatch, getState) => {
      const projectValue = GlobalLicenseCorrectionStore.selectors.projectSelector(getState() as ReduxStore);
      if (projectValue.isLoading || projectValue.value === null || !projectValue.value?.locator) {
        return;
      }

      const overridesValue = GlobalLicenseCorrectionStore.selectors.matchOverridesSelector(getState() as ReduxStore);
      const hasExistingLicenseOverride =
        overridesValue.value?.some((override) => override.matchFilter.type === 'new' && override.newLicenseIdOrRemove === newLicense.licenseId);

      if (overridesValue.isLoading || hasExistingLicenseOverride) {
        return;
      }

      await dispatch(createNewMatchOverride({
        componentProjectId: projectValue.value.locator,
        newLicenseIdOrRemove: newLicense.licenseId,
        matchFilter: {
          type: 'new',
          licenseText: newLicense.licenseText,
          copyrightText: newLicense.copyright,
        },
      }));

      const project = GlobalLicenseCorrectionStore.selectors.projectSelector(getState());
      const revision = GlobalLicenseCorrectionStore.selectors.revisionSelector(getState());

      if (!project.value?.locator || !revision.value?.locator) {
        dispatch(projectLookupError('API Bug: Project or revision locator is missing!'));
        return;
      }

      await Promise.all([
        dispatch(fetchOverrides(project.value?.locator)),
        dispatch(fetchLicenses(revision.value?.locator)),
      ]);
    };
